import "bootstrap/dist/css/bootstrap.css";
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, HashRouter, Navigate } from "react-router-dom";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { jwtDecode } from "jwt-decode";
import "./Fonts/Fonts.Lato.css";
import setting from "./config.json";
import appURL from "./appURL.json";
import TauriLoader from "./components/TauriLoader/TauriLoader";

const root = createRoot(document.getElementById("root"));

const AuthenticationLoader = ({ onComplete }) => {
  const fetchUserInfo = async (token) => {
    try {
      const response = await fetch(
        `${appURL.API_IDENTITY_GetUserInfo}?appCode=${setting.AppCode}&companyCode=${setting.CompanyCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        console.error("Failed to fetch data:", response.status);
        return;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUserPermission = async (token, extID) => {
    try {
      const response = await fetch(
        `${appURL.API_IDENTITY_GetUserPermission}?extID=${extID}&applicationcode=${setting?.AppCode}&companycode=${setting?.CompanyCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        console.error("Failed to fetch data:", response.status);
        return;
      }

      const data = await response.json();

      const permission = JSON.stringify(data?.Permissions);
      const based64Permission = encodeBase64(permission);
      localStorage.setItem("permission", based64Permission);
      return data.Permissions;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSubIdentifiers = async (token, empCode) => {
    try {
      const requestBody = {
        id: "GetAccess",
        params: [
          {
            key: "EmployeeCode",
            value: empCode,
          },
        ],
      };

      const response = await fetch(appURL.API_GetData, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        console.error("Failed to fetch data:", response.status);
        return;
      }

      const data = await response.json();

      let parsed = [];

      if (data?.result?.data) {
        try {
          const codes = JSON.parse(data.result.data);
          parsed = codes?.map((item) => item.Code);
        } catch (err) {
          console.error("Failed to parse result.data:", err);
        }
      }

      return parsed;
    } catch (error) {
      console.log(error);
    }
  };

  const encodeBase64 = (str) => {
    return window.btoa(unescape(encodeURIComponent(str)));
  };

  useEffect(() => {
    const authenticate = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const decodeParam = (param) =>
          param ? decodeURIComponent(param) : null;

        const tkn = decodeParam(params.get("tkn"));
        const devId =
          decodeParam(params.get("devId")) || localStorage.getItem("devId");
        const ext_id =
          decodeParam(params.get("ext_id")) || localStorage.getItem("ext_id");
        const name =
          decodeParam(params.get("name")) || localStorage.getItem("name");
        const title =
          decodeParam(params.get("title")) || localStorage.getItem("title");
        const srv_date =
          decodeParam(params.get("srv_date")) ||
          localStorage.getItem("srv_date");

        const c_id =
          decodeParam(params.get("c_id")) || localStorage.getItem("c_id");
        const c_cd =
          decodeParam(params.get("c_cd")) || localStorage.getItem("c_cd");
        const c_desc =
          decodeParam(params.get("c_desc")) || localStorage.getItem("c_desc");
        const c_str =
          decodeParam(params.get("c_str")) || localStorage.getItem("c_str");
        const c_end =
          decodeParam(params.get("c_end")) || localStorage.getItem("c_end");

        const p_days =
          decodeParam(params.get("p_days")) || localStorage.getItem("p_days");

        const token = tkn || localStorage.getItem("token");

        if (!token) {
          onComplete(false);
          return;
        }

        const payload = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);

        if (payload.exp < now) {
          onComplete(false);
          return;
        }

        const user = await fetchUserInfo(token);
        const permission = await fetchUserPermission(token, ext_id);
        const codelist = await fetchSubIdentifiers(
          token,
          permission?.DataScope?.AccessRange,
        );

        if (user && permission) {
          const UserData = {
            UserInfo: { Data: user },
            UserPermission: { Data: permission },
            CodeList: codelist,
          };
          const UserDataJSON = JSON.stringify(UserData);
          const UserDataBase64 = encodeBase64(UserDataJSON);
          localStorage.setItem("UserData", UserDataBase64);
        }

        localStorage.setItem("token", token);
        localStorage.setItem("devId", devId);
        localStorage.setItem("ext_id", ext_id);
        localStorage.setItem("name", name);
        localStorage.setItem("title", title);
        localStorage.setItem("srv_date", srv_date);

        localStorage.setItem("c_id", c_id);
        localStorage.setItem("c_cd", c_cd);
        localStorage.setItem("c_desc", c_desc);
        localStorage.setItem("c_str", c_str);
        localStorage.setItem("c_end", c_end);

        localStorage.setItem("p_days", p_days);

        // Clean URL so token param disappears
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );

        onComplete(true);
      } catch (error) {
        console.error("Authentication failed", error);
        onComplete(false);
      }
    };

    authenticate();
  }, [onComplete]);

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <TauriLoader
        widthSize="2.5rem"
        heightSize="2.5rem"
        borderSize="0.35rem"
        color="#1D5D9B"
      />
    </div>
  );
};

const Root = () => {
  const [authenticated, setAuthenticated] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthParam = urlParams.has("tkn");

    if (token) {
      try {
        const payload = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp > now) {
          setAuthenticated(true);
          return;
        }
      } catch (e) {
        // invalid token
      }
    }

    if (hasAuthParam) {
      // Run AuthenticationLoader to check for token in URL
      setAuthenticated(null);
    } else {
      // No valid token or auth param — redirect immediately
      setAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated === false) {
      // Redirect to login page if not authenticated
      const currentPath = encodeURIComponent(appURL.APP_BaseURL);
      window.location.href = `${appURL.API_IDENTITY_Authorize}/Identity/Account/Login?returnUrl=${currentPath}`;
    }
  }, [authenticated]);

  if (authenticated === null) {
    return <AuthenticationLoader onComplete={setAuthenticated} />;
  }

  if (authenticated === true) {
    return (
      <HashRouter>
        <App />
      </HashRouter>
    );
  }

  // While redirecting, render nothing
  return null;
};

root.render(<Root />);
reportWebVitals();
